import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  Flag,
  Gamepad2,
  LockKeyhole,
  Radio,
  ShieldCheck,
  Sparkles,
  Trophy,
  Users,
} from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { parseSteamId } from '../../../lib/steamId';
import { COTW_PHASES, DEFAULT_COTW_CONFIG, phaseAt } from '../../../lib/compOfTheWeek';
import {
  currentCotwEventKey,
  getCotwConfig,
  nextCotwEvent,
  publicCotwRoundId,
  requestCotwSignup,
  subscribeCotwEvent,
  subscribeCotwParticipants,
  subscribeCotwRoundResults,
} from '../../../database/compOfTheWeek';
import { mapImageUrl, workshopUrl } from '../../../lib/cs2kz';
import SEO from '../../SEO';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';

const pad = (value) => String(value).padStart(2, '0');

const countdown = (milliseconds) => {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return '00:00:00';
  const total = Math.floor(milliseconds / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const dateLabel = (timestamp, timeZone = 'Europe/Berlin') => {
  if (!Number.isFinite(Number(timestamp))) return 'Saturday at 17:00';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(Number(timestamp)));
};

const phaseCopy = {
  [COTW_PHASES.UPCOMING]: {
    label: 'Next comp',
    title: 'The course is still under lock',
    description: 'A fresh map, course, and movement mode are revealed when the qualifier opens.',
  },
  [COTW_PHASES.QUALIFIER]: {
    label: 'Qualifier live',
    title: 'Twenty minutes. One clean run.',
    description:
      'Every valid finisher moves into the knockout. The live board updates from the trusted worker.',
  },
  [COTW_PHASES.INTERMISSION]: {
    label: 'Round break',
    title: 'The next round is loading',
    description: 'Take a breath. The next knockout round begins after the one-minute break.',
  },
  [COTW_PHASES.KNOCKOUT]: {
    label: 'Knockout live',
    title: 'First valid finish takes the round',
    description:
      'Replay verification includes pause time. The boundary buffer keeps close finishes fair.',
  },
  [COTW_PHASES.COMPLETE]: {
    label: 'Comp complete',
    title: 'That’s the week on the board',
    description:
      'Results are archived with the replay and submission audit for a transparent finish.',
  },
};

const displayNameFor = (row) => row.displayName || row.username || row.name || 'Anonymous runner';

const runtimeFor = (row) => {
  const value = Number(row.runtimeSeconds ?? row.latestRuntimeSeconds ?? row.runtime ?? row.time);
  if (!Number.isFinite(value)) return '—';
  const minutes = Math.floor(value / 60);
  return `${minutes}:${(value % 60).toFixed(3).padStart(6, '0')}`;
};

const SignupPanel = ({ eventId, event, currentUser, userProfile, phase, onSignedUp }) => {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const parsed = useMemo(
    () => parseSteamId(userProfile?.steamId || userProfile?.steamLink || '')?.steamId || null,
    [userProfile]
  );

  const signedUp = Boolean(
    event?.myParticipant || event?.participantIds?.includes(currentUser?.uid)
  );
  const closed = phase !== COTW_PHASES.UPCOMING;

  const submit = async () => {
    setMessage('');
    setError('');
    if (!parsed) {
      setError('Link a canonical SteamID64 or STEAM_ identity in your profile before entering.');
      return;
    }
    setBusy(true);
    try {
      await requestCotwSignup({
        eventId,
        steamId: parsed,
        displayName: userProfile?.username || currentUser?.displayName,
      });
      setMessage('You are on the start list. Join the server at qualifier time.');
      onSignedUp?.();
    } catch (signupError) {
      setError(signupError.message || 'Could not sign you up right now.');
    } finally {
      setBusy(false);
    }
  };

  if (!currentUser) {
    return (
      <Card className="border-heat/40 bg-heat/[0.04]">
        <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
              <LockKeyhole className="h-4 w-4 text-heat" />
              Sign in to enter
            </div>
            <p className="text-sm text-muted-foreground">
              The board is public. Signup is reserved for authenticated runners with a linked
              canonical CS2KZ/Steam identity.
            </p>
          </div>
          <Button asChild variant="outline" className="shrink-0">
            <Link to="/login">
              Sign in <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <Gamepad2 className="h-4 w-4 text-primary" />
          {signedUp ? 'You’re on the start list' : 'Enter this week’s comp'}
        </CardTitle>
        <CardDescription>
          {parsed
            ? `Canonical identity: ${parsed}`
            : 'Link your canonical Steam identity in your profile first.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {signedUp ? (
          <div className="flex items-center gap-2 text-sm text-success">
            <CheckCircle2 className="h-4 w-4" />
            Signup accepted by the trusted worker.
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={submit} disabled={busy || closed || !parsed}>
              {busy ? 'Joining…' : closed ? 'Signup closed' : 'Join the comp'}
              {!busy && !closed && <ArrowRight className="h-4 w-4" />}
            </Button>
            {!parsed && (
              <Button asChild variant="outline">
                <Link to="/profile">Link identity</Link>
              </Button>
            )}
          </div>
        )}
        {message && <p className="text-sm text-success">{message}</p>}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
};

const LiveLeaderboard = ({ participants, phase, roundId }) => {
  const rows = Array.isArray(participants) ? participants : [];
  const [visibleCount, setVisibleCount] = useState(50);
  useEffect(() => setVisibleCount(50), [roundId]);
  const visibleRows = rows.slice(0, visibleCount);
  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b pb-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Trophy className="h-4 w-4 text-heat" />
              Live leaderboard
            </CardTitle>
            <CardDescription>
              {phase === COTW_PHASES.QUALIFIER
                ? 'Verified submissions appear as they arrive.'
                : roundId
                  ? `Public results from ${roundId}.`
                  : 'Public results from the current event.'}
            </CardDescription>
          </div>
          <Badge
            variant={
              phase === COTW_PHASES.QUALIFIER || phase === COTW_PHASES.KNOCKOUT
                ? 'live'
                : 'secondary'
            }
          >
            <Radio className="h-3 w-3" />
            {phase === COTW_PHASES.QUALIFIER || phase === COTW_PHASES.KNOCKOUT
              ? 'Live'
              : 'Standings'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">
            No verified finishes yet. Be the first name on the board.
          </div>
        ) : (
          <ol className="divide-y divide-border">
            {visibleRows.map((row, index) => (
              <li
                key={row.id || row.uid || row.participantId || index}
                className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/30"
              >
                <span className="w-7 shrink-0 font-mono text-xs text-muted-foreground">
                  {row.rank || index + 1}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {displayNameFor(row)}
                </span>
                {row.advanced && <Badge variant="success">Advanced</Badge>}
                {!row.hasResult && row.status === 'signed_up' && (
                  <Badge variant="secondary">Registered</Badge>
                )}
                {!row.hasResult && row.status && row.status !== 'signed_up' && (
                  <Badge variant="secondary">{row.status.replace(/_/g, ' ')}</Badge>
                )}
                {row.hasResult && (
                  <Badge variant={row.accepted === false ? 'secondary' : 'live'}>
                    {row.accepted === false ? 'Unverified' : 'Verified'}
                  </Badge>
                )}
                <span className="font-mono text-sm tabular-nums text-foreground/80">
                  {runtimeFor(row)}
                </span>
              </li>
            ))}
          </ol>
        )}
        {visibleCount < rows.length && (
          <div className="border-t px-5 py-3 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setVisibleCount((count) => count + 50)}
            >
              Show more ({rows.length - visibleCount} remaining)
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CompOfTheWeek = () => {
  const { currentUser, userProfile } = useAuth();
  const [config, setConfig] = useState(DEFAULT_COTW_CONFIG);
  const [eventId, setEventId] = useState('');
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [roundResults, setRoundResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getCotwConfig()
      .then((loadedConfig) => {
        if (!cancelled) setConfig(loadedConfig);
      })
      .catch(() => {
        // Defaults are intentionally usable when the worker has not persisted
        // settings yet.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const key = currentCotwEventKey(now, config);
    setEventId(key);
  }, [now, config]);

  useEffect(() => {
    if (!eventId) return undefined;
    setLoading(true);
    setError('');
    const unsubscribe = subscribeCotwEvent(
      eventId,
      (nextEvent) => {
        setEvent(nextEvent);
        setLoading(false);
      },
      (eventError) => {
        setError(eventError?.message || 'Could not load the CotW event.');
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [eventId]);

  const phase = phaseAt(event, now);
  const publicRoundId = publicCotwRoundId(event, phase);
  useEffect(() => {
    if (!eventId || !publicRoundId) {
      setRoundResults([]);
      return undefined;
    }
    return subscribeCotwRoundResults(eventId, publicRoundId, setRoundResults, () =>
      setRoundResults([])
    );
  }, [eventId, publicRoundId]);

  useEffect(() => {
    if (!eventId) return undefined;
    return subscribeCotwParticipants(eventId, setParticipants, () => setParticipants([]));
  }, [eventId]);

  const scheduledNext = nextCotwEvent(now, config);
  const startsAt = Number(event?.qualifierStartsAt || scheduledNext.startsAt);
  const phaseInfo = phaseCopy[phase] || phaseCopy[COTW_PHASES.UPCOMING];
  const revealed = Boolean(event?.selection && Number(event?.revealedAt || startsAt) <= now);
  const selection = revealed ? event.selection : null;
  const image = selection && mapImageUrl(selection.mapName, selection.courseIndex);
  const round = event?.currentRound;
  const timerTarget =
    phase === COTW_PHASES.QUALIFIER
      ? Number(event?.qualifierEndsAt || now)
      : phase === COTW_PHASES.KNOCKOUT
        ? Number(round?.endsAt || now)
        : phase === COTW_PHASES.INTERMISSION
          ? Number(event?.nextRoundStartsAt || round?.startsAt || startsAt)
          : startsAt;
  const timerLabel =
    phase === COTW_PHASES.QUALIFIER
      ? 'Qualifier closes in'
      : phase === COTW_PHASES.KNOCKOUT
        ? 'Round closes in'
        : phase === COTW_PHASES.INTERMISSION
          ? 'Next round in'
          : 'Qualifier starts in';

  const participantById = new Map(
    participants.map((participant) => [participant.uid || participant.id, participant])
  );
  const resultByParticipant = new Map(roundResults.map((result) => [result.participantId, result]));
  const leaderboardRows = [
    ...participants.map((participant) => {
      const participantId = participant.uid || participant.id;
      const result = resultByParticipant.get(participantId);
      return result
        ? { ...participant, ...result, hasResult: true }
        : { ...participant, hasResult: false };
    }),
    ...roundResults
      .filter((result) => !participantById.has(result.participantId))
      .map((result) => ({ ...result, hasResult: true })),
  ].sort((left, right) => {
    const leftRank = Number.isFinite(Number(left.rank))
      ? Number(left.rank)
      : Number.POSITIVE_INFINITY;
    const rightRank = Number.isFinite(Number(right.rank))
      ? Number(right.rank)
      : Number.POSITIVE_INFINITY;
    return (
      leftRank - rightRank ||
      (Number(left.submittedAt) || Number.POSITIVE_INFINITY) -
        (Number(right.submittedAt) || Number.POSITIVE_INFINITY) ||
      String(left.uid || left.id).localeCompare(String(right.uid || right.id))
    );
  });
  const viewerEvent =
    event && currentUser
      ? {
          ...event,
          myParticipant: participants.find(
            (participant) => (participant.uid || participant.id) === currentUser.uid
          ),
        }
      : event;

  return (
    <div className="relative isolate overflow-hidden">
      <SEO
        title="Comp of the Week"
        description="A weekly CS2KZ race: a fresh course revealed Saturday at 17:00 Berlin time, with verified qualifier and knockout rounds."
        canonicalPath="/comp-of-the-week"
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[34rem] bg-[radial-gradient(ellipse_at_top_right,hsl(var(--heat)/0.14),transparent_62%)]" />
      <div className="mx-auto max-w-screen-xl px-4 pb-16 pt-10 sm:px-6 lg:pt-16">
        <header className="mb-8 grid gap-8 lg:grid-cols-[1fr_22rem] lg:items-end">
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              <span className="h-px w-8 bg-heat" />
              Weekly CS2KZ challenge
              <span className="text-foreground/30">·</span>
              Saturdays / 17:00 Berlin
            </div>
            <div>
              <h1 className="font-display text-5xl font-bold tracking-[-0.04em] sm:text-6xl lg:text-7xl">
                Comp <span className="text-foreground/30">of the Week</span>
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                One surprise course. A twenty-minute qualifier. Then a ruthless, replay-verified
                race to the final four.
              </p>
            </div>
          </div>

          <Card className="border-heat/40 bg-card/80 backdrop-blur">
            <CardContent className="space-y-3 p-5">
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {timerLabel}
                </span>
                <Clock3 className="h-4 w-4 text-heat" />
              </div>
              <div className="font-mono text-4xl font-semibold tabular-nums tracking-tight text-foreground">
                {countdown(timerTarget - now)}
              </div>
              <p className="text-xs text-muted-foreground">
                {dateLabel(startsAt, config.timeZone)} ({config.timeZone})
              </p>
            </CardContent>
          </Card>
        </header>

        {loading ? (
          <div className="space-y-5">
            <div className="h-64 animate-pulse rounded-md border bg-muted/40" />
            <div className="h-32 animate-pulse rounded-md border bg-muted/40" />
          </div>
        ) : error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-6 py-12 text-center text-sm text-destructive">
            {error}
          </div>
        ) : (
          <div className="space-y-6">
            <section className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(20rem,0.65fr)]">
              <Card className="relative min-h-[20rem] overflow-hidden border-foreground/10">
                {image && (
                  <img
                    src={image}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover opacity-35"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/75 to-background/35" />
                <CardContent className="relative flex min-h-[20rem] flex-col justify-between p-6 sm:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <Badge
                      variant={
                        phase === COTW_PHASES.QUALIFIER || phase === COTW_PHASES.KNOCKOUT
                          ? 'live'
                          : 'secondary'
                      }
                    >
                      {phaseInfo.label}
                    </Badge>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      {eventId || 'upcoming'}
                    </span>
                  </div>
                  <div className="max-w-xl">
                    {selection ? (
                      <>
                        <p className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-heat">
                          {selection.modeShort || selection.mode} movement
                        </p>
                        <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
                          {selection.mapName}
                        </h2>
                        <p className="mt-2 text-lg text-foreground/75">{selection.courseName}</p>
                        <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5">
                            <Flag className="h-4 w-4 text-heat" />
                            PRO WR {runtimeFor({ time: selection.proWorldRecord })}
                          </span>
                          {selection.workshopId && (
                            <a
                              href={workshopUrl(selection.workshopId)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
                            >
                              Steam Workshop
                            </a>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-heat">
                          Course reveal at qualifier start
                        </p>
                        <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
                          {phaseInfo.title}
                        </h2>
                        <p className="mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground sm:text-base">
                          {phaseInfo.description}
                        </p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Sparkles className="h-4 w-4 text-heat" />
                      How it works
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm text-muted-foreground">
                    <div className="flex gap-3">
                      <span className="font-mono text-xs text-heat">01</span>
                      <p>
                        <span className="font-medium text-foreground">Qualify.</span> Every valid
                        20-minute finish advances.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <span className="font-mono text-xs text-heat">02</span>
                      <p>
                        <span className="font-medium text-foreground">Race.</span> Knockout rounds
                        run for ten minutes each.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <span className="font-mono text-xs text-heat">03</span>
                      <p>
                        <span className="font-medium text-foreground">Verify.</span> Replay pause
                        time and submission order are audited.
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <div className="flex items-center gap-3 rounded-md border border-border/70 px-4 py-3 text-xs text-muted-foreground">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-success" />
                  Trusted worker scoring · public event projection · no client-side scores
                </div>
              </div>
            </section>

            <SignupPanel
              eventId={eventId}
              event={viewerEvent}
              currentUser={currentUser}
              userProfile={userProfile}
              phase={phase}
            />

            <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
              <LiveLeaderboard
                participants={leaderboardRows}
                phase={phase}
                roundId={publicRoundId}
              />
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Users className="h-4 w-4 text-primary" />
                    Field status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-muted-foreground">Entrants</span>
                    <span className="font-mono text-2xl tabular-nums">
                      {event?.participantCount || participants.length || 0}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-muted-foreground">Still active</span>
                    <span className="font-mono text-2xl tabular-nums">
                      {event?.activeCount ?? '—'}
                    </span>
                  </div>
                  <div className="border-t pt-4 text-xs leading-relaxed text-muted-foreground">
                    The board shows verified public results only. Unverifiable or pause-DQ runs are
                    retained in the private audit trail and never promoted.
                  </div>
                </CardContent>
              </Card>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompOfTheWeek;
